import React, { useCallback, useEffect } from "react";
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "../ui/dialog";
import { useZodForm } from "@/lib/forms/useZodForm";
import { Button } from "../ui/button";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from "../ui/form";
import { Input } from "../ui/input";
import { z } from "zod";
import { errorToast } from "@/lib/utils/ToastUtils";
import { Metric } from "./metricsColumns";
import { trpc } from "@/lib/trpc/client";
import {
  patchKeyedListCache,
  removeFromListBy,
  upsertInListBy,
} from "@trainrr/api-client";

export interface MetricLog {
  value: number;
  logged_at: string;
}

interface EditMetricLogDialogProps {
  clientId: string;
  metric: Metric;
  log?: MetricLog;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formSchema = z.object({
  value: z.coerce.number().finite(),
  date: z.string().min(1),
});

const EditMetricLogDialog: React.FC<EditMetricLogDialogProps> = ({
  open,
  onOpenChange,
  log,
  metric,
  clientId,
}) => {
  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const listInput = {
    clientId,
    metricId: metric.id,
  };
  const utils = trpc.useUtils();

  const patchLogCaches = () => {
    void utils.metrics.listForClient.invalidate({ clientId });
    void utils.metrics.listRecentLogs.invalidate();
    void utils.metrics.chart.invalidate(listInput);
  };

  const saveLog = trpc.metrics.log.useMutation({
    onSuccess: (saved) => {
      patchKeyedListCache(utils.metrics.listLogs, listInput, (old) => {
        const withoutOld = log?.logged_at
          ? removeFromListBy(old, "logged_at", log.logged_at)
          : old;
        return upsertInListBy(withoutOld, saved, "logged_at");
      });
      patchLogCaches();
      onOpenChange(false);
    },
    onError: (error) => {
      errorToast(error, "Error updating metric log");
    },
  });

  const deleteLog = trpc.metrics.deleteLog.useMutation({
    onSuccess: () => {
      if (log?.logged_at) {
        patchKeyedListCache(utils.metrics.listLogs, listInput, (old) =>
          removeFromListBy(old, "logged_at", log.logged_at),
        );
      }
      patchLogCaches();
      onOpenChange(false);
    },
    onError: (error) => {
      errorToast(error, "Error deleting metric log");
    },
  });

  const defaultValues = useCallback(() => {
    return {
      value: log?.value ?? 0,
      date: dateToInputString(
        log?.logged_at ? new Date(log.logged_at) : new Date(),
      ),
    };
  }, [log]);

  const form = useZodForm(formSchema, {
    defaultValues: defaultValues(),
  });

  useEffect(() => {
    form.reset(defaultValues());
  }, [log, form, defaultValues]);

  const onSubmit = (values: z.output<typeof formSchema>) => {
    saveLog.mutate({
      clientId,
      metricId: metric.id,
      value: values.value,
      loggedAt: new Date(values.date).toISOString(),
      oldLoggedAt: log?.logged_at,
    });
  };

  const isPending = saveLog.isPending || deleteLog.isPending;

  return (
    <div>
      <Dialog
        open={open}
        onOpenChange={handleOpenChange}
        analyticsName="edit_metric_log_dialog"
      >
        <DialogContent className="max-w-[800px] max-h-full overflow-y-auto">
          <DialogTitle>{log ? "Update Log" : "Add log"}</DialogTitle>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              <div className="grid gap-4 py-4">
                <FormField
                  control={form.control}
                  name="value"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{metric.name} value</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={metric.unit ?? metric.name}
                          type="number"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          max={dateToInputString(new Date())}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter>
                  {log && (
                    <Button
                      type="button"
                      variant={"destructive"}
                      disabled={isPending}
                      onClick={() => {
                        deleteLog.mutate({
                          clientId,
                          metricId: metric.id,
                          loggedAt: log.logged_at,
                        });
                      }}
                    >
                      Delete
                    </Button>
                  )}
                  <Button type="submit" disabled={isPending}>
                    Save
                  </Button>
                </DialogFooter>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function dateToInputString(date: Date) {
  return new Date(date).toISOString().split("T")[0];
}

export default EditMetricLogDialog;
